<template>
    <div
        class="plan-cycle-card"
        :class="{ 'is-recommended': showRecommendStyle, 'is-disabled': disabled }"
        @click="handleCardClick"
    >
        <!-- 四行与 PlanCycleCardGroup.card-grid 的 subgrid 对齐；推荐角标放在首行内避免多出网格子项 -->
        <div class="card-row card-row-title">
            <div v-if="showRecommendStyle" class="recommend-tag">
                {{ $t('subscribe_plan.recommend') }}
            </div>
            <div class="card-title-row">
                <span class="card-title-main">{{ dataSizeStr }}</span>
                <span class="card-title-sub">/{{ $tc('cycle_unit.month', 1) }}</span>
            </div>
        </div>
        <div class="card-row card-row-cycle">
            <div v-if="showCycleTag" class="card-cycle-tag">{{ cycleTag }}</div>
        </div>
        <div class="card-row card-row-price">
            <div class="card-price-section" :class="{ 'has-price-meta': showPriceMeta }">
                <div class="card-price-row">
                    <span class="card-price-main">{{ salePriceText }}</span>
                    <span v-if="discountBadge" class="card-discount-badge">{{ discountBadge }}</span>
                </div>
                <div v-if="showPriceMeta" class="card-price-meta">
                    <span v-if="originPriceText" class="card-origin-price">{{ originPriceText }}</span>
                    <span v-if="monthlyEquivText" class="card-monthly-equiv">{{ monthlyEquivText }}</span>
                </div>
            </div>
        </div>
        <div class="card-row card-row-tail">
            <reo-common-button
                :label="buttonLabel"
                :disabled="disabled"
                class="card-button"
                @click.native.stop="handleCardButtonClick"
            />
            <div v-if="showFooterDivider" class="card-divider" />
            <div v-if="isFree" class="card-footer card-footer-free" @click.stop>
                <div class="card-terms-row">
                    <reo-checkbox
                        :model-value="!disabled && freeAgreementAccepted"
                        size="small"
                        :isRound="true"
                        :disabled="disabled"
                        @change="onFreeAgreementChange"
                    />
                    <p class="card-terms-text">
                        {{ $t('subscribe.serverRules') }}
                        <a href="/terms-of-use" target="_blank">
                            {{ $t('subscribe.serverRulesName') }}
                        </a>
                    </p>
                </div>
            </div>
            <div v-else-if="showPaidFooter" class="card-footer">
                <rich-text
                    v-if="plan.i18n?.description"
                    class="card-desc"
                    :html="plan.i18n.description"
                    :disabled="disabled"
                    @click.stop="handlePromotionRichTextSubscribe"
                />
                <rich-text
                    v-if="promotionHtml"
                    class="card-promo"
                    :html="promotionHtml"
                    :disabled="disabled"
                    @click.stop="handlePromotionRichTextSubscribe"
                />
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import { EPlanStatus, ESubscribeMode, IPlan, IPromotionItem } from '@reolink-cloud/interface';
import ReoCommonButton from '@/components/ReoCommonButton.vue';
import ReoCheckbox from '@/components/reoCheckbox.vue';
import { unfoldError } from '@/common/js/notice';
import { calcFinalDataStr } from '@/common/js/utils';
import { getPlanPromotionText, isUnlimitedDataSize } from '@/utils/business';
import { durationToTotalMonths, pricePerMonth } from '@/utils/cycleToMonthly';
import type { IPlanCardItem } from './types';

export default Vue.extend({
    name: 'PlanCycleCard',
    components: {
        ReoCommonButton,
        ReoCheckbox
    },
    props: {
        item: { type: Object as PropType<IPlanCardItem>, required: true },
        countryAvailable: { type: Boolean, default: true },
        isCurrentMonthlyCycle: { type: Boolean, required: true },
        promotions: { type: Array as PropType<IPromotionItem[]>, default: () => [] },
        freePlanSubmitting: { type: Boolean, default: false },
        subscribeMode: { type: String as PropType<ESubscribeMode>, required: true },
        currSubPlanId: { type: String, default: '' },
        currSubExpired: { type: Boolean, default: false },
        /** 父组件 runMapper 时递增，用于免费套餐协议勾选恢复默认（与原先 map 重置一致） */
        planCardsDataVersion: { type: Number, required: true }
    },
    data() {

        return {
            freeAgreementAccepted: true
        };

    },
    computed: {
        plan(): IPlan {

            return this.item.plan;

        },
        isFree(): boolean {

            return Number( this.plan.pricing.salePrice ) === 0;

        },
        disabledByPlanRule(): boolean {

            if ( this.plan.status === EPlanStatus.RENEW_ONLY ) return true;
            if ( this.subscribeMode === ESubscribeMode.CREATE ) return false;
            if ( this.subscribeMode === ESubscribeMode.SWITCH && Number( this.plan.pricing.salePrice ) === 0 ) return true;
            return !this.plan.limitations.switchFromOther;

        },
        disabled(): boolean {

            return !this.countryAvailable || this.disabledByPlanRule || ( this.isFree && this.freePlanSubmitting );

        },
        showRecommendStyle(): boolean {

            return this.item.isRecommended && this.countryAvailable && !this.disabledByPlanRule
                && ( this.currSubExpired || this.subscribeMode === ESubscribeMode.CREATE );

        },
        dataSizeStr(): string {

            if ( isUnlimitedDataSize( this.plan )) return `${this.$tc( 'plan.unlimited' )} GB`;
            const rawSize = this.plan?.style['cloud_storage.data_size'] ?? 0;
            return calcFinalDataStr( rawSize, true, 0 );

        },
        showCycleTag(): boolean {

            return !this.isCurrentMonthlyCycle;

        },
        cycleTag(): string {

            const plan = this.plan;
            const { amount, unit } = plan.duration;
            const cycleText = this.$tc( 'cycle_count.' + unit, amount, { count: amount });
            const totalLabel = this.$t( 'dashboard.package_card.total' ) as string;
            if ( isUnlimitedDataSize( plan )) {

                return `${totalLabel} ${this.$tc( 'plan.unlimited' )} / ${cycleText}`;

            }
            const rawSize = plan?.style['cloud_storage.data_size'] ?? 0;
            const totalMonths = durationToTotalMonths( amount, unit );
            const totalBytes = Math.round( rawSize * totalMonths );
            const sizeStr = calcFinalDataStr( totalBytes, true, 0 );
            return `${totalLabel} ${sizeStr} / ${cycleText}`;

        },
        salePriceText(): string {

            return Number( this.plan.pricing.salePrice ) !== 0
                ? `${this.plan.pricing.currency} ${this.plan.pricing.salePrice}`
                : ( this.$t( 'plan.free' ) as string );

        },
        originPriceText(): string {

            if ( Number( this.plan.pricing.salePrice ) < Number( this.plan.pricing.regularPrice )) {

                return `${this.plan.pricing.currency} ${this.plan.pricing.regularPrice}`;

            }
            return '';

        },
        discountBadge(): string {

            const r = Number( this.plan.pricing.regularPrice );
            const s = Number( this.plan.pricing.salePrice );
            if ( r <= 0 || s >= r ) return '';
            const pct = Math.round(( 1 - s / r ) * 100 );
            if ( pct === 0 ) return '';
            return `${pct}% Off`;

        },
        monthlyEquivText(): string {

            if ( this.isCurrentMonthlyCycle ) return '';
            const s = Number( this.plan.pricing.salePrice );
            if ( s === 0 ) return '';
            const { amount, unit } = this.plan.duration;
            const perMonth = pricePerMonth( s, amount, unit );
            return `≈${this.plan.pricing.currency}${perMonth.toFixed( 2 )}${this.$tc( 'subscribe.traffic_des_table.month' )}`;

        },
        showPriceMeta(): boolean {

            return !!( this.originPriceText || this.monthlyEquivText );

        },
        buttonLabel(): string {

            if ( !this.countryAvailable ) return this.$t( 'subscribe.not_available' ) as string;
            if ( this.isFree ) return this.$tc( 'plan.claim_now' );
            if ( this.subscribeMode === ESubscribeMode.SWITCH && !this.currSubExpired ) {

                return this.$tc( 'email.expired.switch_now' );

            }
            return this.$tc( 'cloud.noVerify.subscribe' );

        },
        promotionHtml(): string | undefined {

            return getPlanPromotionText( this.promotions, this.plan );

        },
        showPaidFooter(): boolean {

            if ( this.isFree ) return false;
            return !!( this.plan.i18n?.description || this.promotionHtml );

        },
        showFooterDivider(): boolean {

            if ( this.isFree ) return true;
            return this.showPaidFooter;

        }
    },
    watch: {
        planCardsDataVersion() {

            if ( this.isFree ) {

                this.freeAgreementAccepted = true;

            }

        }
    },
    methods: {
        handleCardClick() {

            if ( this.disabled ) return;
            this.$emit( 'change', this.plan.id );

        },
        handleCardButtonClick() {

            this.handleSubscribeClick( false );

        },
        /** 营销文案内 <apply-button /> 点击：与主按钮区分，用于购物车侧按促销 usageLimitToItems 预填数量 */
        handlePromotionRichTextSubscribe() {

            this.handleSubscribeClick( true );

        },
        handleSubscribeClick( fromPromotionRichText: boolean ) {

            if ( this.disabled ) return;
            this.$emit( 'change', this.plan.id );
            if ( this.isFree ) {

                if ( !this.freeAgreementAccepted ) {

                    unfoldError( 'errors.agree_first', 2000 );
                    return;

                }
                this.$emit( 'claim-free-plan', this.plan );
                return;

            }
            const isCurrentPlan = this.currSubPlanId === this.plan.id && !this.currSubExpired;
            if ( !isCurrentPlan ) {

                this.$emit( 'subscribe', { fromPromotionRichText });

            }

        },
        onFreeAgreementChange( checked: boolean ) {

            this.freeAgreementAccepted = checked;

        }
    }
});
</script>

<style lang="less" scoped>
.plan-cycle-card {
    /* 与 PlanCycleCardGroup .card-grid 共享四行高度（subgrid） */
    display: grid;
    grid-template-rows: subgrid;
    grid-row: span 4;
    grid-template-columns: minmax(0, 1fr);
    background: @white;
    border: 1px solid @font_tertiary;
    border-radius: 8px;
    padding: 24px;
    box-sizing: border-box;
    transition: border-color 0.2s, box-shadow 0.2s;
    position: relative;
    min-width: 0;
    &.is-recommended {
        /* 渐变描边：mask 只显示 2px 环，与卡片高度无关；主区为白底 + 径向高光，随高度铺满 */
        border: none;
        padding: 24px;
        border-radius: 0px 8px 8px 8px;
        background-color: @white;
        background-image: radial-gradient(140% 140% at 0% 0%, rgba(72, 198, 239, 0.3) 1%, rgba(243, 244, 245, 0) 99%);
        &::before {
            content: '';
            position: absolute;
            inset: 0;
            z-index: 0;
            border-radius: inherit;
            padding: 2px;
            background: linear-gradient(137deg, #005BEA 0%, rgba(0, 94, 234, 0.9946) 37%, rgba(0, 198, 251, 0.8) 75%, rgba(0, 198, 251, 0.6) 100%);
            -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
            -webkit-mask-composite: xor;
            mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
            mask-composite: exclude;
            pointer-events: none;
        }
        > .card-row {
            position: relative;
            z-index: 1;
        }
        .card-button{
            /deep/ button{
                background: linear-gradient(281deg, #00C6FB 0%, #005BEA 100%);
                border-width: 0;
                transition: background 0.3s;
                &:hover{
                    background: linear-gradient(281deg, #00B2E6 0%, #004FD1 100%);
                }
            }
        }
    }
    &.is-disabled {
        opacity: 0.7;
        cursor: not-allowed;
    }
    .card-row-title {
        position: relative;
        min-width: 0;
    }
    .recommend-tag {
        font-size: 12px;
        line-height: 17px;
        color: @white;
        font-weight: 500;
        background: linear-gradient(282deg, rgba(0, 198, 251, 0.8) -1%, rgba(0, 91, 234, 0.8) 100%);
        border-radius: 12px 812px 0px 0px;
        height: 21px;
        position: absolute;
        z-index: 2;
        top: -44px;
        left: -24px;
        text-align: center;
        padding: 2px 10px;
        box-sizing: border-box;
        .mobile({
            font-size: 12px;
        });
    }
    .card-row {
        min-width: 0;
    }
    .card-row-tail {
        display: flex;
        flex-direction: column;
        min-width: 0;
    }
    .card-title-row {
        display: flex;
        align-items: baseline;
        gap: 4px;
        flex-wrap: wrap;
    }
    .card-title-main {
        font-family: @font_02;
        font-size: 28px;
        font-weight: 600;
        color: @font_primary;
        line-height: 1.2;
    }
    .card-title-sub {
        font-family: @font_02;
        font-size: 16px;
        font-weight: 500;
        color: @font_secondary;
        line-height: 1.2;
    }
    .card-row-cycle {
        display: flex;
        align-items: flex-start;
    }
    .card-cycle-tag {
        display: inline-block;
        background: @b_1;
        color: @b_6;
        font-size: 12px;
        font-weight: 400;
        padding: 4px 8px;
        border-radius: 2px;
        margin-top: 4px;
        width: fit-content;
        max-width: 100%;
    }
    .card-row-price {
        padding-top: 24px;
    }
    .card-price-section {
        display: flex;
        flex-direction: column;
        justify-content: flex-end;
    }
    .card-price-row {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-shrink: 0;
    }
    .card-price-main {
        font-size: 22px;
        font-weight: 600;
        color: @font_primary;
    }
    .card-discount-badge {
        background: @orange_4;
        color: @white;
        font-size: 12px;
        font-weight: 500;
        padding: 2px 8px;
        border-radius: 20px;
    }
    .card-price-meta {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-top: auto;
        font-size: 14px;
        font-weight: 500;
        color: @font_secondary;
        line-height: 20px;
    }
    .card-origin-price {
        text-decoration: line-through;
    }
    .card-monthly-equiv {
        color: @orange_4;
    }
    .card-row-tail .card-button {
        margin-top: 12px;
    }
    .card-divider {
        height: 1px;
        background: #E1E1E1;
        margin: 12px 0;
    }
    .card-footer {
        .card-desc,
        .card-promo {
            font-size: 14px;
            color: @font_secondary;
        }
    }
    .card-footer-free {
        .card-terms-row {
            display: flex;
            align-items: flex-start;
            gap: 8px;
        }
        .card-terms-text {
            margin: 0;
            font-size: 14px;
            color: @font_secondary;
            line-height: 20px;
        }
    }
}
</style>
